import { useState } from "react";
import styled from "styled-components";
import { useIntl } from "react-intl";
import { Button } from "src/design-system/components/Button/Button";
import { spacing } from "src/design-system/tokens/spacing";
import { screenMinWidth } from "src/theme";
import { scrollbarHiddenStyling } from "src/utils/styles/common";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { LineName, type LineNameProps } from "src/components/LineName/LineName";
import moreMessages from "src/components/BottomNavbar/BottomNavbar.messages";
import messages from "src/domain/HotelsScreen/HotelFilter/FilterComponents/CheckboxList.messages";

type Props = {
  lineNames: LineNameProps[];
  noButton?: boolean;
};

const INITIAL_NUMBER_LINES = 2;

export function CarrierLineNames({ lineNames, noButton = false }: Props) {
  const intl = useIntl();
  const { isMobile } = useScreenMinWidth();
  const [showMoreLines, setShowMoreLines] = useState(false);
  const canShowMore = lineNames.length > INITIAL_NUMBER_LINES && !isMobile;

  const displayedLineNames =
    showMoreLines || isMobile
      ? lineNames
      : lineNames.slice(0, INITIAL_NUMBER_LINES);

  const showMoreMessage = `+ ${lineNames.length - INITIAL_NUMBER_LINES} ${intl
    .formatMessage(moreMessages.more)
    .toLowerCase()}`;

  return (
    <Wrapper $showMoreLines={showMoreLines}>
      <StyledLineNames $showMoreLines={showMoreLines}>
        {displayedLineNames.map((lineName) => (
          <StyledLineName key={lineName.name} {...lineName} />
        ))}
      </StyledLineNames>
      {canShowMore &&
        (!noButton ? (
          <StyledButton
            variant="secondary"
            onPress={() => setShowMoreLines((prev) => !prev)}
            size="large"
          >
            <span className="text-label-sm">
              {showMoreLines
                ? intl.formatMessage(messages.showLess)
                : showMoreMessage}
            </span>
          </StyledButton>
        ) : (
          <span className="text-label-sm">{showMoreMessage}</span>
        ))}
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $showMoreLines: boolean }>`
  display: flex;
  gap: ${spacing.sm};
  align-items: ${({ $showMoreLines }) =>
    $showMoreLines ? "flex-start" : "center"};
  flex-direction: ${({ $showMoreLines }) =>
    $showMoreLines ? "column" : "row"};
`;

const StyledLineNames = styled.div<{ $showMoreLines: boolean }>`
  overflow-y: hidden;
  overflow-x: auto;
  white-space: nowrap;
  display: flex;
  gap: ${spacing.sm};
  ${scrollbarHiddenStyling}

  ${screenMinWidth.sm} {
    white-space: unset;
    flex-wrap: ${({ $showMoreLines }) => ($showMoreLines ? "wrap" : "nowrap")};
  }
`;

const StyledLineName = styled(LineName)`
  flex-shrink: 0;

  ${screenMinWidth.sm} {
    flex-shrink: 1;
  }
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
  padding: ${spacing.sm} ${spacing.md};
`;
